import { Icon } from "@iconify/react";
import React, { useEffect, useRef, useState } from "react";
import { PiEye, PiEyeSlash } from "react-icons/pi";
import { twMerge } from "tailwind-merge";
import { sanitizeRichText } from "../../utils/richText";

const LoginInput = ({
  id,
  name,
  type = "text",
  label,
  value,
  placeholder,
  error,
  onChange,
  onBlur,
  showPasswordToggle,
  isPasswordVisible,
  togglePasswordVisibility,
  className,
  position,
  isRequired = true,
  isFloatingLabel = true,
  labelStyle = "",
  inputStyle = "",
  outerContainerStyle = "",
  tooltip,
  isDisabled = false,
  maxLength,
  allowHtml = false,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const richTextRef = useRef(null);
  const isFloating = Boolean(value) || isFocused;

  const resizeRichTextInput = () => {
    const element = richTextRef.current;
    if (!element) return;

    const computedStyle = window.getComputedStyle(element);
    const minHeight = parseFloat(computedStyle.minHeight) || 0;
    const maxHeight = parseFloat(computedStyle.maxHeight) || Infinity;

    element.style.height = "auto";

    const nextHeight = Math.max(minHeight, Math.min(element.scrollHeight, maxHeight));
    element.style.height = `${nextHeight}px`;
    element.style.overflowY = element.scrollHeight > maxHeight ? "auto" : "hidden";
  };

  useEffect(() => {
    if (!allowHtml || !richTextRef.current) return;

    const sanitizedValue = sanitizeRichText(value || "");
    if (richTextRef.current.innerHTML !== sanitizedValue) {
      richTextRef.current.innerHTML = sanitizedValue;
    }

    resizeRichTextInput();
  }, [allowHtml, value]);

  const getBorderClasses = () => {
    if (error) return "border-red-200";
    if (isDisabled) return "border-medium-gray";
    if (isFocused || Boolean(value)) return "border-blue-shades";
    return "border-medium-gray";
  };

  if(type === "textarea"){ // retuning textarea element
    return (
      <div className={twMerge("relative mb-7", outerContainerStyle)}>
        {label && isFloatingLabel && (
          <div className="flex flex-row items-baseline gap-3">
            <label
              htmlFor={id}
              className={twMerge(
                "absolute left-3 px-1 ease-in-out",
                isFloating
                  ? "-top-3 z-10 bg-white font-medium"
                  : "top-4 text-transparent",
                error ? "text-red-400" : "text-blue-shades"
              )}
            >
              {label}
            </label>
            {
              tooltip && 
              <div className="relative group cursor-pointer">
                <Icon
                  icon="mdi:help-circle-outline"
                  className="text-[#6B7280]"
                  width={18}
                  height={18}
                />
                <div className="absolute z-20 hidden group-hover:block bg-black text-white text-sm 
                rounded-md px-2 py-1 top-full mt-1 left-1/2 -translate-x-1/2 
                min-w-[300px] break-words text-center whitespace-normal">
                  {tooltip}
                </div>
              </div>
            }
          </div>
        )}
        {label && !isFloatingLabel && (
          <div className="flex flex-row items-baseline gap-3 mb-3">
            <label
              htmlFor={id}
              className={`form-label block font-inter text-secondary text-level-3 font-medium leading-[24px] ${labelStyle} `}
            >
              {label} {isRequired && <span className="text-light-red">*</span>}
            </label>
              {
                tooltip && 
                <div className="relative group cursor-pointer">
                  <Icon
                    icon="mdi:help-circle-outline"
                    className="text-[#6B7280]"
                    width={18}
                    height={18}
                  />
                  <div className="absolute z-20 hidden group-hover:block bg-black text-white text-sm 
                  rounded-md px-2 py-1 top-full mt-1 left-1/2 -translate-x-1/2 
                  min-w-[300px] break-words text-center whitespace-normal">
                    {tooltip}
                  </div>
                </div>
              }
          </div>
        )}
        <div className="relative">
          {allowHtml ? (
            <div
              id={id}
              ref={richTextRef}
              role="textbox"
              aria-label={label}
              aria-multiline="true"
              contentEditable={!isDisabled}
              suppressContentEditableWarning
              data-placeholder={placeholder}
              onInput={(e) => {
                const sanitizedValue = sanitizeRichText(e.currentTarget.innerHTML);
                onChange?.({ target: { id, name, value: sanitizedValue } });
                resizeRichTextInput();
              }}
              onPaste={(e) => {
                e.preventDefault();
                const html = e.clipboardData.getData("text/html");
                const text = e.clipboardData.getData("text/plain");
                const pastedValue = html ? sanitizeRichText(html) : text;
                document.execCommand(html ? "insertHTML" : "insertText", false, pastedValue);
                requestAnimationFrame(resizeRichTextInput);
              }}
              onFocus={() => setIsFocused(true)}
              onBlur={(e) => {
                setIsFocused(false);
                const sanitizedValue = sanitizeRichText(e.currentTarget.innerHTML);
                if (richTextRef.current) richTextRef.current.innerHTML = sanitizedValue;
                if (onBlur) onBlur({ target: { id, name, value: sanitizedValue } });
              }}
              className={twMerge(
                `w-full px-2 py-4 rounded-lg font-inter text-[15px] h-[48px] leading-[27px] text-black/40 transition-all focus:outline-none border scrollbar-none overflow-y-auto empty:before:content-[attr(data-placeholder)] empty:before:text-gray/60 [&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:list-disc [&_ul]:pl-6 [&_li]:my-1 [&_p]:my-1 [&_u]:underline ${className} ${getBorderClasses()}`,
                inputStyle,
                "text-black/80"
              )}
            />
          ) : (
            <textarea
              id={id}
              name={name}
              type={
                type === "password" && showPasswordToggle
                  ? isPasswordVisible
                    ? "text"
                    : "password"
                  : type
              }
              value={value}
              placeholder={placeholder}
              maxLength={maxLength}
              onChange={onChange}
              onFocus={() => setIsFocused(true)}
              onBlur={(e) => {
                setIsFocused(false);
                if (onBlur) onBlur(e);
              }}
              className={twMerge(
                `w-full px-2 py-4 rounded-lg font-inter text-[15px] h-[48px] leading-[27px] text-black/40 transition-all focus:outline-none border scrollbar-none  ${className} ${getBorderClasses()}`,
                type === "password" ? "pr-10" : "",
                inputStyle,
                "placeholder:text-gray/60", // Light color for placeholder text
                "text-black/80" // Dark color for input text
              )}
            />
          )}
        </div>
        {showPasswordToggle && (
          <button
            type="button"
            onClick={togglePasswordVisibility}
            className={`bg-white absolute text-black/40 top-1/2 right-2 transform -translate-y-1/2 w-auto px-3 ${position}`} // Adjusted right position
          >
            {isPasswordVisible ? <PiEye size={20} /> : <PiEyeSlash size={20} />}
          </button>
        )}
  
        {error && (
          <p className="mt-1 pl-0 text-xs text-red-400 absolute -bottom-[20px]">
            {error}
          </p>
        )}
      </div>
    );
  }else{ // returning non textarea element
    return (
      <div className={twMerge("relative mb-7", outerContainerStyle)}>
        {label && isFloatingLabel && (
          <div className="flex flex-row items-baseline gap-3">
            <label
              htmlFor={id}
              className={twMerge(
                "absolute left-3 px-1 ease-in-out",
                isFloating
                  ? "-top-3 z-10 bg-white font-medium"
                  : "top-4 text-transparent",
                error ? "text-red-400" : "text-blue-shades"
              )}
            >
              {label}
            </label>
              {
                tooltip && 
                <div className="relative group cursor-pointer">
                  <Icon
                    icon="mdi:help-circle-outline"
                    className="text-[#6B7280]"
                    width={18}
                    height={18}
                  />
                  <div className="absolute z-20 hidden group-hover:block bg-black text-white text-sm 
                  rounded-md px-2 py-1 top-full mt-1 left-1/2 -translate-x-1/2 
                  min-w-[300px] break-words text-center whitespace-normal">
                    {tooltip}
                  </div>
                </div>
              }
          </div>
        )}
        {label && !isFloatingLabel && (
         <div className="flex flex-row items-center gap-2 mb-2">
            <label
              htmlFor={id}
              className={`form-label font-inter text-secondary text-level-3 font-medium leading-[24px] ${labelStyle}`}
            >
              {label} {isRequired && <span className="text-light-red">*</span>}
            </label>

            {tooltip && (
              <div className="relative group cursor-pointer">
                <Icon
                  icon="mdi:help-circle-outline"
                  className="text-[#6B7280]"
                  width={18}
                  height={18}
                />
                <div className="absolute z-20 hidden group-hover:block bg-black text-white text-sm 
                rounded-md px-2 py-1 top-full mt-1 left-1/2 -translate-x-1/2 
                min-w-[300px] break-words text-center whitespace-normal">
                  {tooltip}
                </div>
              </div>
            )}
          </div>

        )}
        <div className="relative">
          <input
            id={id}
            name={name}
            type={
              type === "password" && showPasswordToggle
                ? isPasswordVisible
                  ? "text"
                  : "password"
                : type
            }
            value={value}
            placeholder={placeholder}
            maxLength={maxLength}
            onChange={isDisabled ? undefined : onChange}
            onFocus={() => setIsFocused(true)}
            onBlur={(e) => {
              setIsFocused(false);
              if (onBlur) onBlur(e);
            }}
            disabled = {isDisabled}
            className={twMerge(
              `w-full px-2 py-4 rounded-lg font-inter text-[15px] h-[48px] leading-[27px] text-black/40 transition-all focus:outline-none border ${className} ${getBorderClasses()}`,
              type === "password" ? "pr-10" : "",
              inputStyle,
              "placeholder:text-gray/60", // Light color for placeholder text
              "text-black/80" // Dark color for input text
            )}
          />
        </div>
        {showPasswordToggle && (
          <button
            type="button"
            onClick={togglePasswordVisibility}
            className={`bg-white absolute text-black/40 top-1/2 right-2 transform -translate-y-1/2 w-auto px-3 ${position}`} // Adjusted right position
          >
            {isPasswordVisible ? <PiEye size={20} /> : <PiEyeSlash size={20} />}
          </button>
        )}
  
        {error && (
          <p className="mt-1 pl-0 text-xs text-red-400 absolute -bottom-[20px]">
            {error}
          </p>
        )}
      </div>
    );
  }
};

export default LoginInput;

{/* <textarea
placeholder="Leave feedback about the delivered work"
value={feedback}
onChange={(e) => {setFeedback(e.target.value)}}
className="w-full text-inter text-gray text-level-4 rounded-[5px] border border-medium-gray p-3 focus:outline-none mt-[32px] min-h-[166px]"
rows="4"
></textarea> */}
